import { UserStatistic } from './../apiclient/apiclient_generated';
import { Action, getModule, Module, VuexModule, Mutation } from 'vuex-module-decorators';
import store from '@/store/index';
import { WS_URL as ApiUrl } from '@/config';
import axios from 'axios';

import {
  ActiveEntitiesQuery,
  CreateLicenseCommand,
  ExportLicensesCommand,
  LicenseClient,
  SendLicenseEmailCommand,
  UpdateLicenseCommand
} from '@/apiclient/apiclient_generated';
import { NotificationService } from '@/services/notification-service';
import LicenseStoreState from '@/types/LicenseStoreState';

@Module({ dynamic: true, namespaced: true, store, name: 'license' })
export class LicenseModule extends VuexModule {
  private licenseClient = new LicenseClient(ApiUrl, axios);

  selectedAccountLicenses = [];
  availableProducts = [];
  licenseTypes = [];

  @Action
  async export(command: ExportLicensesCommand) {
    return this.licenseClient.export(command).then((data) => data);
  }

  @Action
  async sendLicenseEmail(command: SendLicenseEmailCommand) {
    return await this.licenseClient.sendLicenseEmail(command);
  }

  @Action
  async createLicense(command: CreateLicenseCommand) {
    return await this.licenseClient
      .createLicense(command)
      .then((data) => {
        if (data.isSuccess) {
          NotificationService.NotifySuccess('License was successfully created.');
        } else {
          NotificationService.NotifyErrors(data.errors);
        }
        return data;
      })
      .catch((error) => {
        if (error.status === 400) {
          NotificationService.NotifyErrors(Object.values(error.response.errors));
        }
        return error;
      });
  }

  @Action
  async updateLicense(command: UpdateLicenseCommand) {
    return await this.licenseClient
      .updateLicense(command)
      .then((data) => {
        if (data.isSuccess) {
          NotificationService.NotifySuccess('License was updated created.');
        } else {
          NotificationService.NotifyErrors(data.errors);
        }
        return data;
      })
      .catch((error) => {
        if (error.status === 400) {
          NotificationService.NotifyErrors(Object.values(error.response.errors));
        }
        return error;
      });
  }

  @Action
  async deleteLicense(licenseId) {
    const result = await this.licenseClient.deleteLicense(licenseId);

    if (result.isSuccess) {
      this.processLicenseDelete(licenseId);
      NotificationService.NotifySuccess('License was successfully removed.');
      return result.isSuccess;
    } else {
      NotificationService.NotifyErrors(result.errors);
    }
  }

  @Action
  async getAccountLicenses(accountId) {
    const result = await this.licenseClient.getAccountLicenses(accountId);

    this.setAccountLicenses(result);
  }

  @Action
  async getAvailableProducts() {
    const products = await this.licenseClient.getAvailableProducts();

    this.setProducts(products);
  }

  @Action
  async getAvailableLicenseTypes() {
    const licenseTypes = await this.licenseClient.getAvailableLicenseTypes();

    this.setLicenseTypes(licenseTypes);
  }

  @Mutation
  setAccountLicenses(licenses) {
    if (licenses && licenses.length) {
      licenses.forEach((l) => {
        if (l.createdDate) {
          l.createdDate = new Date(l.createdDate);
        }
        if (l.expiryDate) {
          l.expiryDate = new Date(l.expiryDate);
        }
      });
    }

    this.selectedAccountLicenses = licenses;
  }

  @Mutation
  setProducts(products) {
    this.availableProducts = products;
  }

  @Mutation
  setLicenseTypes(licenseTypes) {
    this.licenseTypes = licenseTypes;
  }

  @Mutation
  processLicenseDelete(id) {
    const index = this.selectedAccountLicenses.findIndex((t) => t.id === id);

    if (index > -1) {
      this.selectedAccountLicenses.splice(index, 1);
    }
  }

  storeState = {} as LicenseStoreState;

  @Mutation
  updateStoreState(params: LicenseStoreState) {
    this.storeState.selectedAccount = params.selectedAccount;
  }
}

export const LicenseStore = getModule(LicenseModule);
